/**
 * screens-quality.ts — คุณภาพข้อมูลและ Audit (T099)
 *
 * รวมคิวที่ต้องจัดการก่อนเชื่อรายงาน:
 * - SKU ไม่มีต้นทุน (ทำให้ P&L incomplete)
 * - รายการรายได้/ค่าใช้จ่ายที่ต้องตรวจสอบ (needs_review)
 * - ทรัพย์สินที่ยังไม่ยืนยัน (needs_review → ยังไม่กระทบ P&L)
 * - รายการภาษีที่ยังไม่ยืนยัน (pnl_impact=false)
 * และ Audit log (ผู้แก้ เวลา ค่าเดิม/ใหม่ เหตุผล request id)
 */
import type { Page } from './router.js';
import { getTenant } from './state.js';
import * as api from './api.js';
import {
  esc,
  money,
  pageShell,
  dataTable,
  pill,
  opt,
  errorBox,
  pagerHtml,
  bindPager,
  showDrawer,
  drawerHtml,
  fieldLabel,
} from './ui.js';

type Row = Record<string, unknown>;

const ENTITY_LABELS: Record<string, string> = {
  financial_entries: 'รายการรายได้/ค่าใช้จ่าย',
  payroll_summaries: 'สรุปเงินเดือน',
  fixed_assets: 'ทรัพย์สิน',
  tax_ledger: 'ทะเบียนภาษี',
  purchases: 'การซื้อสินค้า',
  standard_cost_history: 'ต้นทุนมาตรฐาน',
  allocation_rules: 'กติกาการปันส่วน',
  accounting_periods: 'งวดบัญชี',
  skus: 'สินค้า/SKU',
  sku_channel_aliases: 'รหัสสินค้าจากช่องทาง',
};
const ACTION_LABELS: Record<string, string> = {
  create: 'สร้าง',
  update: 'แก้ไข',
  override: 'แก้ไขทับ',
  revision: 'เวอร์ชันใหม่',
  reversal: 'กลับรายการ',
  delete_denied: 'ปฏิเสธการลบ',
};

function queueCard(title: string, id: string, hint: string): string {
  return `<section class="card"><h3>${esc(title)}</h3><p class="foot-note">${esc(hint)}</p><div id="${id}">กำลังโหลด…</div></section>`;
}

export const dataQualityPage: Page = {
  title: 'คุณภาพข้อมูลและประวัติการแก้ไข (Audit)',
  render: () =>
    pageShell(
      'คุณภาพข้อมูลและ Audit',
      `<div id="summary" class="kpis"></div>
      ${queueCard('SKU ที่ยังไม่มีต้นทุน', 'q-sku', 'ต้องกรอกต้นทุนก่อน รายงานจึงจะไม่ถูกทำเครื่องหมาย “ข้อมูลยังไม่สมบูรณ์”')}
      ${queueCard('รายการที่ต้องตรวจสอบ', 'q-entries', 'รายการที่นำเข้า/กรอกแล้วยังไม่ยืนยันการจัดหมวด')}
      ${queueCard('ทรัพย์สินรอตรวจ', 'q-assets', 'การซื้ออุปกรณ์ที่ยังไม่ยืนยันว่าเป็นทรัพย์สิน — ยังไม่กระทบ P&L จนกว่าจะอนุมัติ')}
      ${queueCard('ภาษีที่ยังไม่ยืนยัน', 'q-tax', 'VAT/WHT/แบบภาษี ไม่ลดกำไรอัตโนมัติ จนกว่าฝ่ายบัญชีจะยืนยัน mapping')}
      ${queueCard('งวดบัญชี', 'q-periods', 'งวดที่ปิดแล้วแก้ทับไม่ได้ — ต้องใช้รายการปรับปรุง/กลับรายการ')}
      <section class="card">
        <h3>Audit log</h3>
        <form id="audit-filter" class="grid-form" role="search">
          <label>ตาราง<select name="entityTable"><option value="">ทั้งหมด</option>${Object.entries(
            ENTITY_LABELS,
          )
            .map(([v, l]) => `<option value="${v}">${l}</option>`)
            .join('')}</select></label>
          <label>การกระทำ<select name="action"><option value="">ทั้งหมด</option>${Object.entries(
            ACTION_LABELS,
          )
            .map(([v, l]) => `<option value="${v}">${l}</option>`)
            .join('')}</select></label>
          <div class="form-actions"><button type="submit">กรอง</button></div>
        </form>
        <div id="audit-list">กำลังโหลด…</div>
      </section>
      ${drawerHtml}`,
    ),
  mount(root) {
    if (!getTenant()) return;
    const el = (id: string): HTMLElement => root.querySelector<HTMLElement>(`#${id}`)!;
    const counts: Record<string, number> = {};

    const renderSummary = (): void => {
      const cards: [string, string, number][] = [
        ['SKU ไม่มีต้นทุน', 'sku', counts['sku'] ?? 0],
        ['รายการรอตรวจ', 'entries', counts['entries'] ?? 0],
        ['ทรัพย์สินรอตรวจ', 'assets', counts['assets'] ?? 0],
        ['ภาษีรอยืนยัน', 'tax', counts['tax'] ?? 0],
      ];
      el('summary').innerHTML = cards
        .map(
          ([label, , n]) =>
            `<div class="kpi"><div class="label">${esc(label)}</div><div class="value ${n > 0 ? 'value-warn' : ''}">${n}</div></div>`,
        )
        .join('');
    };

    const loadSkuQueue = async (): Promise<void> => {
      try {
        const res = await api.listSkus({ missingCostOnly: true, limit: 50 });
        counts['sku'] = res.missingCostCount;
        renderSummary();
        el('q-sku').innerHTML = dataTable(
          ['รหัส SKU', 'ชื่อ', 'หน่วย', ''],
          res.items.map((s) => ({
            cls: 'row-danger',
            cells: [
              esc(s.skuCode),
              esc(s.name ?? '—'),
              esc(s.unit),
              `<a class="btn-link" href="#skucost">กรอกต้นทุน →</a>`,
            ],
          })),
          'ทุก SKU มีต้นทุนแล้ว ✓',
        );
      } catch (e) {
        el('q-sku').innerHTML = errorBox(e);
      }
    };

    const loadEntryQueue = async (): Promise<void> => {
      try {
        const { items, total } = await api.listEntries({ reviewStatus: 'needs_review', limit: 50 });
        counts['entries'] = total ?? items.length;
        renderSummary();
        el('q-entries').innerHTML = dataTable(
          ['วันที่', 'ประเภท', 'คู่ค้า', 'ยอดรวม', ''],
          items.map((r: Row) => ({
            cls: 'row-warn',
            cells: [
              esc(r['event_date'] ?? '—'),
              esc(r['classification']),
              esc(r['counterparty_name'] ?? '—'),
              money(r['total_amount']),
              `<a class="btn-link" href="#entries">จัดการ →</a>`,
            ],
          })),
          'ไม่มีรายการค้างตรวจ ✓',
        );
      } catch (e) {
        el('q-entries').innerHTML = errorBox(e);
      }
    };

    const loadAssetQueue = async (): Promise<void> => {
      try {
        const { items } = await api.listAssets('needs_review');
        counts['assets'] = items.length;
        renderSummary();
        el('q-assets').innerHTML = dataTable(
          ['ชื่อ', 'ราคาทุน', 'วันที่ซื้อ', ''],
          items.map((r: Row) => ({
            cls: 'row-warn',
            cells: [
              esc(r['name']),
              money(r['cost']),
              esc(r['purchase_date'] ?? '—'),
              `<a class="btn-link" href="#assets">ตรวจสอบ →</a>`,
            ],
          })),
          'ไม่มีทรัพย์สินค้างตรวจ ✓',
        );
      } catch (e) {
        el('q-assets').innerHTML = errorBox(e);
      }
    };

    const loadTaxQueue = async (): Promise<void> => {
      try {
        const { items } = await api.listTaxLedger();
        const pending = items.filter((r: Row) => r['pnl_impact'] !== true);
        counts['tax'] = pending.length;
        renderSummary();
        el('q-tax').innerHTML = dataTable(
          ['เดือนภาษี', 'ประเภท', 'ยอดภาษี', 'สถานะ', ''],
          pending.map((r: Row) => ({
            cells: [
              esc(r['tax_month'] ?? '—'),
              esc(r['kind']),
              money(r['tax_amount']),
              pill('ไม่กระทบ P&L (ค่าเริ่มต้น)'),
              `<a class="btn-link" href="#tax">ยืนยัน →</a>`,
            ],
          })),
          'ไม่มีรายการภาษีค้างยืนยัน ✓',
        );
      } catch (e) {
        el('q-tax').innerHTML = errorBox(e);
      }
    };

    const loadPeriods = async (): Promise<void> => {
      try {
        const { items } = await api.listPeriods();
        el('q-periods').innerHTML = dataTable(
          ['ปี', 'เดือน', 'สถานะ', 'ปิดเมื่อ'],
          items.map((r: Row) => ({
            cells: [
              esc(r['year']),
              esc(r['month'] ?? 'ทั้งปี'),
              r['status'] === 'closed'
                ? pill('ปิดงวดแล้ว', 'warn')
                : r['status'] === 'adjusted'
                  ? pill('เปิดเพื่อปรับปรุง', 'warn')
                  : pill('เปิดอยู่', 'ok'),
              esc(r['closed_at'] ?? '—'),
            ],
          })),
          'ยังไม่มีการปิดงวด',
        );
      } catch (e) {
        el('q-periods').innerHTML = errorBox(e);
      }
    };

    const auditState = { limit: 25, offset: 0 };
    const auditForm = root.querySelector<HTMLFormElement>('#audit-filter')!;
    const loadAudit = async (): Promise<void> => {
      try {
        const { items, total } = await api.listAudit({
          ...(opt(auditForm, 'entityTable') ? { entityTable: opt(auditForm, 'entityTable')! } : {}),
          ...(opt(auditForm, 'action') ? { action: opt(auditForm, 'action')! } : {}),
          limit: auditState.limit,
          offset: auditState.offset,
        });
        el('audit-list').innerHTML =
          dataTable(
            ['เวลา', 'การกระทำ', 'รายการ', 'ผู้แก้ไข', 'เหตุผล', 'request id', ''],
            items.map((a) => ({
              cells: [
                esc(a.createdAt.replace('T', ' ').slice(0, 19)),
                esc(ACTION_LABELS[a.action] ?? a.action),
                esc(ENTITY_LABELS[a.entityTable] ?? a.entityTable),
                esc(a.actorId ?? '—'),
                esc(a.reason ?? '—'),
                `<code>${esc(a.requestId ?? '—')}</code>`,
                `<button class="mini" data-audit="${esc(a.id)}">ดูค่าเดิม/ใหม่</button>`,
              ],
            })),
            'ยังไม่มี audit log',
          ) + pagerHtml(total, auditState.limit, auditState.offset);
        bindPager(el('audit-list'), auditState, loadAudit);
        el('audit-list')
          .querySelectorAll<HTMLButtonElement>('button[data-audit]')
          .forEach((b) =>
            b.addEventListener('click', () => {
              const a = items.find((x) => x.id === b.dataset['audit']);
              if (!a) return;
              showDrawer(
                root,
                `${ACTION_LABELS[a.action] ?? a.action} · ${ENTITY_LABELS[a.entityTable] ?? a.entityTable}`,
                diffTable(a.oldData, a.newData),
              );
            }),
          );
      } catch (e) {
        el('audit-list').innerHTML = errorBox(e);
      }
    };
    auditForm.addEventListener('submit', (e) => {
      e.preventDefault();
      auditState.offset = 0;
      void loadAudit();
    });

    renderSummary();
    void loadSkuQueue();
    void loadEntryQueue();
    void loadAssetQueue();
    void loadTaxQueue();
    void loadPeriods();
    void loadAudit();
  },
};

function diffTable(oldData: unknown, newData: unknown): string {
  const o = (oldData ?? {}) as Record<string, unknown>;
  const n = (newData ?? {}) as Record<string, unknown>;
  const keys = [...new Set([...Object.keys(o), ...Object.keys(n)])].filter(
    (k) => JSON.stringify(o[k] ?? null) !== JSON.stringify(n[k] ?? null),
  );
  if (keys.length === 0) return '<p class="muted">ไม่มีฟิลด์ที่เปลี่ยน</p>';
  return dataTable(
    ['ฟิลด์', 'ค่าเดิม', 'ค่าใหม่'],
    keys.map((k) => ({
      cells: [esc(fieldLabel(k)), esc(fmt(o[k])), esc(fmt(n[k]))],
    })),
  );
}

function fmt(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}
